/**
 * header-nav.js
 * Desktop dropdown controller for the site header.
 *
 * Disclosure semantics, not a menubar: each trigger is a <button> with
 * aria-expanded, and its panel is a sibling in the same <li>, so Tab moves
 * trigger -> first panel link with no focus management.
 *
 * Differences from the mega-menu controller this replaces, each fixing a real
 * defect in it:
 *
 *   - Timers are controller-level, not per-item. Per-item closures meant that
 *     moving from one nav item to the next ran A's 200ms close against B's
 *     open timer, so the scrim strobed.
 *   - Hover is gated on pointerType === 'mouse'. pointerenter also fires for
 *     touch, so the first tap opened the panel and the following click
 *     immediately toggled it shut.
 *   - One `openPanel` reference holds the single-open invariant, instead of
 *     re-querying the document on every open.
 *   - focusout is bound on the controller root, so tabbing from the last link
 *     of panel A into trigger B doesn't close-then-reopen.
 *   - Closes on a breakpoint change: a panel left open while resizing below
 *     the desktop switch would otherwise hold the scrim over an unusable page.
 *   - Hovering a childless top-level item closes the open panel. `open()`
 *     returns early when an item has no panel, so without an explicit close
 *     the previous panel stayed lit while the pointer sat on a sibling.
 *
 * Closed panels use `inert` rather than `hidden`. `hidden` sets display:none,
 * which cancels the transition; `inert` removes the panel from the tab order
 * and the accessibility tree while leaving it styleable. `inert` and
 * data-open must never disagree - toggle them together.
 *
 * Contract: [data-nav-item] on every top-level <li>; the ones with a dropdown
 * additionally hold [data-nav-trigger] + [data-nav-panel], which is how the
 * two kinds are told apart. Plus [data-header-scrim], and data-nav-open
 * reflected on the <nav> root.
 */
(function () {
  'use strict';

  // 0 = open on the frame the pointer arrives. The hover-intent delay this
  // replaces made the first dropdown of a session feel broken; the trade is
  // that sweeping the cursor across the nav row now pops a panel in passing.
  // The machinery is kept so restoring a small intent delay is a one-line
  // change rather than a rewrite.
  var OPEN_DELAY = 0;
  var CLOSE_DELAY = 200;
  var DESKTOP = '(min-width: 990px)';

  function init(nav) {
    if (nav.__navAbort) nav.__navAbort.abort();
    var ac = (nav.__navAbort = new AbortController());
    var signal = ac.signal;

    var header = nav.closest('[data-header]') || document;
    var scrim = header.querySelector ? header.querySelector('[data-header-scrim]') : null;

    var openPanel = null;
    var openTimer = null;
    var closeTimer = null;

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      openTimer = closeTimer = null;
    }

    function setScrim(on) {
      if (scrim) scrim.toggleAttribute('data-open', on);
      nav.toggleAttribute('data-nav-open', on);
    }

    function close(item) {
      if (!item) return;
      var trigger = item.querySelector('[data-nav-trigger]');
      var panel = item.querySelector('[data-nav-panel]');
      if (!panel) return;
      panel.setAttribute('data-open', 'false');
      panel.setAttribute('inert', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (openPanel === item) openPanel = null;
      setScrim(false);
    }

    function closeAll() {
      clearTimers();
      nav.querySelectorAll('[data-nav-item]').forEach(close);
      openPanel = null;
      setScrim(false);
    }

    function open(item) {
      if (!item || openPanel === item) return;
      var trigger = item.querySelector('[data-nav-trigger]');
      var panel = item.querySelector('[data-nav-panel]');
      if (!panel) return;
      if (openPanel) close(openPanel);
      panel.removeAttribute('inert');
      panel.setAttribute('data-open', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      openPanel = item;
      setScrim(true);
    }

    nav.addEventListener(
      'pointerenter',
      function (event) {
        if (event.pointerType !== 'mouse') return;
        var item = event.target.closest && event.target.closest('[data-nav-item]');
        if (!item || !nav.contains(item)) return;
        clearTimers();
        // A childless top-level item (Home Sleep Study, Pricing & Insurance)
        // is still a destination: arriving on it has to dismiss whatever is
        // open. Without this the pending close was cancelled by clearTimers()
        // above and open() then bailed on `if (!panel) return`, so the
        // previous panel and its scrim sat over the page until the pointer
        // left the nav entirely.
        if (!item.querySelector('[data-nav-panel]')) {
          closeAll();
          return;
        }
        // Already warm: switch instantly. Deferring here makes the nav feel
        // sticky when moving along the row.
        if (!OPEN_DELAY || (openPanel && openPanel !== item)) open(item);
        else openTimer = setTimeout(function () { open(item); }, OPEN_DELAY);
      },
      { capture: true, signal: signal }
    );

    nav.addEventListener(
      'pointerleave',
      function (event) {
        if (event.pointerType !== 'mouse') return;
        clearTimers();
        closeTimer = setTimeout(closeAll, CLOSE_DELAY);
      },
      { signal: signal }
    );

    nav.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest('[data-nav-trigger]');
        if (!trigger) return;
        event.preventDefault();
        clearTimers();
        var item = trigger.closest('[data-nav-item]');
        if (openPanel === item) close(item);
        else open(item);
      },
      { signal: signal }
    );

    nav.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Escape' || !openPanel) return;
        var trigger = openPanel.querySelector('[data-nav-trigger]');
        closeAll();
        if (trigger) trigger.focus();
      },
      { signal: signal }
    );

    nav.addEventListener(
      'focusout',
      function (event) {
        // Safari reports a null relatedTarget when the window loses focus;
        // closing on that would collapse the panel behind the user's back.
        if (!event.relatedTarget) return;
        if (!nav.contains(event.relatedTarget)) closeAll();
      },
      { signal: signal }
    );

    if (scrim) {
      scrim.addEventListener('pointerdown', closeAll, { signal: signal });
    }

    var mq = window.matchMedia(DESKTOP);
    var onChange = function (event) {
      if (!event.matches) closeAll();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange, { signal: signal });

    signal.addEventListener('abort', clearTimers);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.header__nav').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });

  // Theme editor: a panel left open while the merchant edits elsewhere would
  // sit over the preview.
  document.addEventListener('shopify:section:select', function (event) {
    event.target.querySelectorAll('.header__nav').forEach(function (nav) {
      if (nav.__navAbort) init(nav);
    });
  });
})();
